import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import { upperFirst, get } from 'lodash'

import { PERMISSION } from '../../Constants';
import { dateComparison, stringComparison, checkPermission, boolComparison } from './utils';
import { useFormatMessage } from './hooks'
import { DataContext, UserContext } from "./context"

const dateFormat = 'DD-MM-YYYY HH:mm';

const baselineColumns = ['createDate', 'location', 'lastMessageSent', 'currentState', 'deliveryBranchId', 'modifyDate']

const dataIndexToKey = dataIndex => Array.isArray(dataIndex) ? dataIndex.join(".") : dataIndex

const renderRentalCar = value => value ? 'X' : ''

const renderOrderType = value => value ? upperFirst(value) : ''

const renderDate = value => dayjs(value).format(dateFormat)

const useLastMessageRenderer = () => {
  const f = useFormatMessage()
  return lastMessageSent => lastMessageSent == null
    ? f('ActionDeliveries.NoMessage')
    : dayjs(lastMessageSent.timeSentUTC).format(dateFormat)
}

const useCurrentStateRenderer = () => {
  const f = useFormatMessage()
  return currentState => f(currentState.stateId)
}

const useBranchNameRenderer = () => {
  const { branches } = useContext(DataContext)
  return deliveryBranchId => branches.find(b => b.id === deliveryBranchId)?.name
}

/**
 * useColumns hook for delivery tables
 * @param {Array<String>} baseColumns that are always used
 * @returns {Array<Object>} columns for the current user
 * 'title' and 'key' props are auto-generated based on 'dataIndex'
 *
 * NOTE:
 *  use FormattedMessage in 'title' because of the BUG: https://my.origo.is/browse/ZMIDSD-262
 *  do not use FormattedMessage in 'render', because it's not working when Export to Excel
 */
export const useColumns = (baseColumns = baselineColumns) => {
  const f = useFormatMessage()
  const renderLastMessage = useLastMessageRenderer()
  const renderCurrentState = useCurrentStateRenderer()
  const renderBranchName = useBranchNameRenderer()

  const { allowedColumns, branches, senders } = useContext(DataContext)
  const { permissions } = useContext(UserContext)

  const columns = [
    {
      dataIndex: ['recipient', 'name'],
      sorter: (a, b) => stringComparison(a.recipient?.name, b.recipient?.name),
      sortDirections: ['ascend', 'descend']
    },
    {
      dataIndex: ['recipient', 'identificationNumber'],
      sorter: (a, b) => stringComparison(a.recipient?.identificationNumber, b.recipient?.identificationNumber),
      sortDirections: ['ascend', 'descend']
    },
    {
      dataIndex: 'rentalCar',
      sorter: (a, b) => boolComparison(a.rentalCar, b.rentalCar),
      sortDirections: ['ascend', 'descend'],
      render: renderRentalCar,
    },
    {
      dataIndex: 'orderType',
      render: renderOrderType,
      sorter: (a, b) => stringComparison(a.orderType, b.orderType),
      sortDirections: ['ascend', 'descend'],
    },
    {
      dataIndex: 'senderOrderId',
      sorter: (a, b) => stringComparison(a.senderOrderId, b.senderOrderId),
      sortDirections: ['ascend', 'descend'],
      width: 140,
    },
    {
      dataIndex: 'businessKey',
      sorter: (a, b) => stringComparison(a.businessKey, b.businessKey),
      sortDirections: ['ascend', 'descend'],
      width: 140,
    },
    {
      dataIndex: 'createDate',
      render: renderDate,
      sorter: (a, b) => dateComparison(a.createDate, b.createDate),
      sortDirections: ['ascend', 'descend']
    },
    {
      dataIndex: 'location',
      sorter: (a, b) => stringComparison(a.location, b.location),
    },
    {
      dataIndex: ['recipient', 'street'],
      sorter: (a, b) => stringComparison(a.street, b.street),
    },
    {
      dataIndex: 'lastMessageSent',
      render: renderLastMessage,
      sorter: (a, b) => {
        const aTime = a.lastMessageSent?.timeSentUTC
        const bTime = b.lastMessageSent?.timeSentUTC

        if (!aTime && !bTime) return 0
        if (!aTime) return -1
        if (!bTime) return 1

        return dateComparison(aTime, bTime)
      },
      sortDirections: ['ascend', 'descend']
    },
    {
      dataIndex: 'currentState',
      render: renderCurrentState,
      sorter: (a, b) => stringComparison(f(a.currentState.stateId), f(b.currentState.stateId)),
      sortDirections: ['ascend', 'descend'],
    },
    {
      dataIndex: ['sender', 'name'],
      sorter: (a, b) => stringComparison(a.sender?.name, b.sender?.name),
      sortDirections: ['ascend', 'descend']
    },
    {
      dataIndex: 'description',
      sorter: (a, b) => stringComparison(a.description, b.description),
      sortDirections: ['ascend', 'descend']
    },
    {
      dataIndex: ['branch', 'id'],
      render: renderBranchName,
      sorter: (a, b) => stringComparison(
        renderBranchName(a.deliveryBranchId), renderBranchName(b.deliveryBranchId)
      ),
    },
    {
      dataIndex: 'modifyDate',
      render: renderDate,
      sorter: (a, b) => dateComparison(a.modifyDate, b.modifyDate),
      sortDirections: ['ascend', 'descend'],
    },
    {
      dataIndex: ['properties', 'licensePlate'],
      sorter: (a, b) => stringComparison(a.properties?.licensePlate, b.properties?.licensePlate),
      sortDirections: ['ascend', 'descend'],
      render: value => value ?? '_MISSING',
    },
    {
      dataIndex: ['properties', 'arrivalTime'],
      sorter: (a, b) => dateComparison(a.properties?.arrivalTime, b.properties?.arrivalTime),
      sortDirections: ['ascend', 'descend'],
      render: value => value ?? '_MISSING',
    },
    {
      dataIndex: ['properties', 'departureTime'],
      sorter: (a, b) => dateComparison(a.properties?.departureTime, b.properties?.departureTime),
      sortDirections: ['ascend', 'descend'],
      render: value => value ?? '_MISSING',
    },
    // paymentAmount no longer displayed in tables
  ]

  const shouldInclude = col => {
    const key = dataIndexToKey(col.dataIndex)
    return baseColumns.includes(key) || allowedColumns.get(key)?.used
  }

  const shouldShow = col => {
    const key = dataIndexToKey(col.dataIndex)
    return baseColumns.includes(key) || allowedColumns.get(key)?.required
  }

  const filterColumn = col => {
    if (col.key === 'orderType')
      return shouldInclude(col) && checkPermission(permissions, PERMISSION.readCollection)
    if (col.key === 'deliveryBranchId')
      return shouldInclude(col) && branches.length > 1
    if (col.key === 'sender.name')
      return shouldInclude(col) && senders.length > 1
    return shouldInclude(col)
  }

  const filtered = columns.filter(filterColumn)

  const mapped = filtered.map(col => {
    const key = dataIndexToKey(col.dataIndex)
    return {
      ...col,
      key,
      title: <FormattedMessage id={`DeliveryColumn.${key}`} />,
      visible: shouldShow(col),
    }
  })

  return mapped
}

/** Needed for mobile table view */
export const useRenderColumn = () => {
  const renderLastMessage = useLastMessageRenderer()
  const renderCurrentState = useCurrentStateRenderer()
  const renderBranchName = useBranchNameRenderer()

  return (item, key) => {
    const value = get(item, key, '')
    switch (key) {
      case 'lastMessageSent': return renderLastMessage(value)
      case 'currentState': return renderCurrentState(value)
      case 'rentalCar': return renderRentalCar(value)
      case 'orderType': return renderOrderType(value)
      case 'createDate':
      case 'modifyDate': return renderDate(value)
      case 'deliveryBranchId': return renderBranchName(value)
      default: return value
    }
  }
}

export const searchableProps = [
  ['recipient', 'name'],
  'senderOrderId',
  'location',
  ['currentState', 'name'],
  'orderType'
]
